import { Countries } from '@bridebook/models';
import type { IGroup } from '@bridebook/models/source/models/Countries/Groups.types';
import type { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { useQuery } from '@tanstack/react-query';

/**
 * The country's group taxonomy: the categories checklist tasks and budget costs are filed
 * under. The app fills `state.app.groups` only after the wedding-update and user-setup
 * actions, so on some routes it is empty. The planner reads its own copy, as it does for
 * country costs. The read is cache-first (a Firestore named query from IndexedDB), with
 * infinite staleness, since a taxonomy changes only every few months.
 */
export const useCountryGroupsQuery = (
  country: string | null | undefined,
): { groups: Record<string, IGroup> | undefined; settled: boolean; failed: boolean } => {
  const { data, isFetched, isError } = useQuery(
    ['ai-planner', 'country-groups', country],
    () => Countries._.getById(country as CountryCodes).getGroups(),
    { enabled: Boolean(country), staleTime: Infinity, cacheTime: Infinity },
  );

  // Nothing to fetch without a country, so that counts as settled.
  return { groups: data, settled: !country || isFetched, failed: isError };
};

export const useCountryGroups = (
  country: string | null | undefined,
): Record<string, IGroup> | undefined => useCountryGroupsQuery(country).groups;
